/**
 * The Concept swap-test bar (`hook-specificity.ts`), applied to the paid ad.
 *
 * ITC Tax run 4: the Concept hook carried a $5M Texas data-center result, and
 * the ad the engine wrote from it shipped the headline "Challenge inflated
 * valuations." and a lead that named nothing a competitor could not run. The
 * Concept had to clear this bar; the ad written from it did not.
 *
 * ITC Tax run 5: "Challenge Generic Digital Infrastructure Valuations" and
 * "Data Center Property Tax Review" passed, because Title Case runs of common
 * words matched words the evidence capitalises in a results tile. A headline
 * now needs a figure, a named place, an abbreviation or a proper noun the
 * evidence states; a category label built from common words is not one.
 *
 * The headline and the lead of the main text (its first sentence, cut at the
 * feed's fold) must each carry a specific the evidence states. Pure. The caller
 * decides what a finding does (the claims gate treats it as blocking).
 */

import type { AdCopyClaimsField, AdCopyClaimsFinding } from './ad-copy-claims';
import { hookSpecificity } from './hook-specificity';
import { resultFigures } from './result-evidence';
import { relevanceTo } from './result-strength';
import { scopedClientKinds } from './result-tile-attribution';
import { moneyOrPercentSpans } from './unevidenced-figures';

const US_STATE_WORDS = new Set(
  (
    'alabama alaska arizona arkansas california colorado connecticut delaware florida georgia hawaii idaho illinois indiana iowa kansas kentucky louisiana maine maryland massachusetts michigan minnesota mississippi missouri montana nebraska nevada ' +
    'ohio oklahoma oregon pennsylvania tennessee texas utah vermont virginia washington wisconsin wyoming'
  ).split(' '),
);

/** Abbreviations that name a credential or a role, not a mechanism. */
const NOT_A_NAMED_TERM = new Set(
  'cpa cmi cfa cfe mai asa jd cfo ceo coo cto vp llc llp inc nda faq usa roi seo'.split(' '),
);

/** Business nouns a results tile or a sentence start happens to capitalise. */
const COMMON_BUSINESS_WORDS = new Set(
  (
    'savings saving review reviews valuation valuations appeal appeals assessment assessments tax taxes property asset assets service services solution solutions ' +
    'data center centers digital infrastructure generic challenge opinion second memo report reduction results result energy renewables equipment life ' +
    'request learn more contact get free confidential county parish borough'
  ).split(' '),
);

/** Where the feed folds the main text behind "see more". */
export const LEAD_FOLD_CHARS = 150;

export interface LeadSpecificityContext {
  readonly brandName: string | null;
  readonly productNames: readonly string[];
  readonly competitorNames: readonly string[];
}

/** The first sentence, held to the fold; a longer sentence is judged on what is visible. */
export function leadOf(text: string): string {
  const first = text.trim().split(/(?<=[.!?])\s+(?=[A-Z0-9"'“(])/)[0] ?? '';
  return first.length > LEAD_FOLD_CHARS ? first.slice(0, LEAD_FOLD_CHARS) : first;
}

/**
 * Only the characters a `u`-flag pattern allows to be escaped. `\-` and `\&` are SyntaxErrors in
 * `u` mode, so a hyphenated word ("Multi-State", ITC run 9) threw out of the gate, and the throw
 * read as a provider failure that sent the copy to the fallback model.
 */
export function wholeWord(word: string): RegExp {
  const escaped = word.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&');
  return new RegExp(`(?<![\\p{L}\\p{N}])${escaped}(?![\\p{L}\\p{N}])`, 'u');
}

/** The first word of every sentence or tile fragment, which capitalisation says nothing about. */
function sentenceInitialWords(lines: readonly string[]): Set<string> {
  const out = new Set<string>();
  for (const line of lines) {
    for (const sentence of line.split(/(?<=[.!?])\s+/)) {
      const first = sentence.trim().match(/^[\p{L}\p{N}&/-]+/u)?.[0];
      if (first) out.add(first);
    }
  }
  return out;
}

const NAMED_PLACE = /\b[A-Z][a-z]+(?:\s[A-Z][a-z]+)?\s(?:County|Parish|Borough)\b/g;

/**
 * A county, parish or borough the evidence names ("Harris County, Texas"). Its
 * words are capitalised at the start of a results-tile fragment, so the
 * proper-noun test below reads them as sentence-initial and drops them.
 */
function namesPlaceTheEvidenceStates(headline: string, evidenceText: string): boolean {
  return (headline.match(NAMED_PLACE) ?? []).some((place) => evidenceText.includes(place));
}

/**
 * What a headline may count as a specific on LinkedIn: a figure the evidence
 * states, an abbreviation it states (CIP, BPP, GPU), a US state it names, a
 * proper noun it capitalises mid-sentence and never writes in lower case, or a
 * client kind with the scope a results tile gives it ("multi-state data center
 * developer").
 */
function headlineIsSpecific(
  headline: string,
  evidence: readonly string[],
  context: LeadSpecificityContext,
): boolean {
  const evidenceText = evidence.join('\n');
  const evidenceFigures = new Set(resultFigures(evidenceText));
  if (resultFigures(headline).some((figure) => evidenceFigures.has(figure))) return true;
  // A plain count the evidence states ("5 to 20 clients", "36 business sales") is as specific as a price.
  if (
    (headline.match(/\b\d[\d,]*\b/g) ?? []).some((count) => wholeWord(count).test(evidenceText))
  ) {
    return true;
  }

  const brandWords = new Set(
    [context.brandName ?? '', ...context.productNames, ...context.competitorNames]
      .flatMap((name) => name.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [])
      .map((word) => word.replace(/s$/, '')),
  );
  const lowerEvidence = evidenceText.toLowerCase();
  const lowerHeadline = headline.toLowerCase();
  if (scopedClientKinds(evidence).some((kind) => lowerHeadline.includes(kind))) return true;
  if (namesPlaceTheEvidenceStates(headline, evidenceText)) return true;
  const initials = sentenceInitialWords(evidence);

  for (const raw of headline.match(/[\p{L}\p{N}&/-]+/gu) ?? []) {
    const token = raw.replace(/^[&/-]+|[&/-]+$/g, '');
    const lower = token.toLowerCase();
    if (token.length < 3 || brandWords.has(lower.replace(/s$/, ''))) continue;
    if (NOT_A_NAMED_TERM.has(lower) || COMMON_BUSINESS_WORDS.has(lower)) continue;
    const stated = wholeWord(token).test(evidenceText);
    if (/^[A-Z][A-Z0-9&/]{2,}$/.test(token)) {
      if (stated) return true;
      continue;
    }
    if (US_STATE_WORDS.has(lower) && wholeWord(lower).test(lowerEvidence)) return true;
    if (!/^[A-Z]/.test(token) || !stated || initials.has(token)) continue;
    const writtenLower = new RegExp(
      wholeWord(lower).source + '|' + wholeWord(lower + 's').source,
      'u',
    );
    if (!evidence.some((line) => writtenLower.test(line))) return true;
  }
  return false;
}

const FIGURE_RAW =
  /[$£€]\s?\d(?:[\d,]*\d)?(?:\.\d+)?\s?(?:million|billion|thousand|[kmb]\b)?|\d(?:[\d,]*\d)?(?:\.\d+)?\s?%/gi;

/**
 * The specifics the evidence states that a LinkedIn lead may open with: named
 * places first, then money and percentage figures, in evidence order, deduped.
 * The refusal below names a few so a reader knows what the headline could have
 * carried; the LinkedIn prompt lists them up front so the drafter copies one
 * instead of inventing a plausible figure the gate then refuses (demo QA
 * 2026-11-03: "18 months", "16 December", neither in the evidence).
 */
export function statedLeadSpecifics(evidence: readonly string[], limit = 8): string[] {
  const text = evidence.join('\n');
  const places = [...new Set(text.match(NAMED_PLACE) ?? [])];
  const figures = [...new Set((text.match(FIGURE_RAW) ?? []).map((figure) => figure.trim()))];
  return [...places, ...figures].slice(0, limit);
}

/** A few specifics the evidence states, so a refusal says what a headline could have carried. */
function statedSpecifics(evidence: readonly string[]): string {
  const found = statedLeadSpecifics(evidence, 4);
  return found.length > 0 ? ` The evidence states ${found.join(', ')}.` : '';
}

export function findGenericAdLead(
  fields: readonly AdCopyClaimsField[],
  evidence: readonly string[],
  context: LeadSpecificityContext,
  platform: 'linkedin' | 'meta' | 'google_rsa',
): AdCopyClaimsFinding[] {
  // Meta hooks are legitimately identity call-outs and scene-openers
  // (HOOK_FRAMEWORKS) and an RSA headline is a keyword; this is the LinkedIn
  // lead-gen bar.
  if (platform !== 'linkedin') return [];
  const checked: Array<{ name: string; text: string; field: string }> = [];
  for (const { field, text } of fields) {
    if (!text.trim()) continue;
    if (field === 'headline') {
      checked.push({ name: 'headline', text, field });
    } else if (field === 'commentary') {
      checked.push({ name: 'opening line', text: leadOf(text), field });
    }
  }

  const findings: AdCopyClaimsFinding[] = [];
  for (const item of checked) {
    const specific =
      item.name === 'headline'
        ? headlineIsSpecific(item.text, evidence, context)
        : hookSpecificity({
            hook: item.text,
            evidence,
            brandName: context.brandName,
            productNames: context.productNames,
            offerNames: [],
            competitorNames: context.competitorNames,
          }).specific;
    if (specific) continue;
    findings.push({
      field: item.field,
      detail: `The ${item.name === 'headline' ? 'headline' : 'opening line of the intro text'} carries nothing specific to this brand ("${item.text.trim()}"): ${item.name === 'headline' ? 'no figure, place, abbreviation or scoped client kind the evidence states' : "no figure, named mechanism or place, or run of the source's own words"}. A competitor could run it unchanged.${item.name === 'headline' ? statedSpecifics(evidence) : ''}`,
      fix:
        item.name === 'headline'
          ? `Rewrite the headline so it carries one thing a competitor could not run unchanged: a figure the evidence states, a named place (a state or county), an abbreviation the evidence uses (such as "CIP" or "BPP"), or a client kind with the scope the results line gives it (such as "multi-state data center developer"). A client's result figure may lead the headline only framed as that client's ("$10M property tax saved for a Texas energy client"), never promised to the reader. A Title Case run of common words ("Data Center Property Tax Review", "Challenge Generic Digital Infrastructure Valuations") is a category label, not a specific. An offer or a general promise ("Get a second opinion", "Challenge inflated valuations") is not a specific either.`
          : `Rewrite the opening line of the intro text so it carries one specific the evidence states: a result figure attributed to the brand's own report, or the source's own named mechanism, place or phrase, copied as the source words it. A general promise about the reader's problem is not a specific.`,
    });
  }
  return findings;
}

/**
 * The headline of an ad written from a Concept must be about that Concept. ITC run 9: the energy
 * $10M Harris County Concept, pitched to CFOs, shipped the headline "Identify LHI and BPP tax
 * roll misassignments", true and sourced and about none of its proof. The headline shares the
 * Concept hook's figure or one of its subject words (its client kind, place or named mechanism),
 * or it is not this Concept's headline. A quality finding, not a claims one: the copy is true, it
 * is just about something else, so it is repaired once and otherwise ships flagged.
 */
export function findHeadlineOffConcept(
  fields: readonly AdCopyClaimsField[],
  conceptHook: string | null | undefined,
  platform: 'linkedin' | 'meta' | 'google_rsa',
): AdCopyClaimsFinding[] {
  const hook = conceptHook?.trim();
  if (platform !== 'linkedin' || !hook) return [];
  const headline = fields.find((field) => field.field === 'headline')?.text.trim();
  if (!headline) return [];
  const figureKey = (text: string): Set<string> =>
    new Set(moneyOrPercentSpans(text).map((span) => `${span.kind}:${span.value}`));
  const hookFigures = figureKey(hook);
  if ([...figureKey(headline)].some((key) => hookFigures.has(key))) return [];
  if (relevanceTo(headline, hook)) return [];
  return [
    {
      field: 'headline',
      detail: `The headline ("${headline}") is not about this Concept: it states none of the figure, place or subject of the Concept's hook ("${hook}").`,
      fix: `Rewrite the headline around what the Concept's hook carries: its figure, its place or its client kind. The hook is: "${hook}"`,
      sentence: headline,
    },
  ];
}
